import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Heading } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { useRoomsContext } from '../../contexts/roomsContext';
import RemoveRoomButton from '../RemoveRoomButton/RemoveRoomButton';
import bindRoomIndexToUpdateTravelersUtil from '../../utils/bindRoomIndexToUpdateTravelersUtil';
import StepperRow from '../StepperRow/StepperRow';
import ChildAgeNote from '../ChildrenAges/ChildAgeNote';
import IncludePets from '../IncludePets/IncludePets';
import {
  DEFAULT_ADULT_TRAVELING,
  DEFAULT_CHILDREN_TRAVELING,
  DEFAULT_INFANTS_TRAVELING,
  MIN_ADULT_TRAVELING,
  MIN_CHILDREN_TRAVELING,
  MIN_INFANTS_TRAVELING,
} from '../../constants/travelersConstants';
import { useConfigContext } from '../../contexts/configContext';
import NotesSection from '../NotesSection/NotesSection';
import { useStepperMaximum } from '../../hooks/useStepperMaximum';
import ChildrenAgesVariant from '../ChildrenAges/ChildrenAgesVariant';
import InfantsVariant from '../ChildrenAges/InfantsVariant';

const RoomVariant = memo(({ isMultiRoom, canRemove, roomIndex }) => {
  const {
    rooms,
    updateAdults,
    updateChildCount,
    updateInfantCount,
    updatePets,
  } = useRoomsContext();

  const {
    hasInfants,
    hasPets,
    hasChildrenAges,
    hasRooms,
  } = useConfigContext();

  const { formatMessage } = useIntl();

  const {
    adults, children, infants, includePets,
  } = rooms[roomIndex];

  const roomId = roomIndex + 1;

  const groupHeadingLabel = formatMessage({ id: 'roomNumber' }, { roomId });
  const globalisedAdultAria = formatMessage({ id: 'adult' });
  const globalisedChildAria = formatMessage({ id: 'child' });
  const globalisedInfantAria = formatMessage({ id: 'infant' });
  const childAgeRangeLabel = useMemo(() => (hasInfants
    ? 'child_age_range'
    : 'child_age_range_including_infants'), [hasInfants]);
  const {
    maxAdultsForStepper,
    maxChildrenForStepper,
    maxInfantsForStepper,
  } = useStepperMaximum(roomIndex);
  return (
    <div role="group" id="rooms-traveler-modal">
      {roomIndex > 0 && (
        <hr className="margin-b" data-testid="room-line-break" />
      )}
      {isMultiRoom && (
        <div
          className="flex flex-justify-between flex-align-center"
          style={{ height: '48px' }}
        >
          <Heading level={2} variant="sans-xsmall-bold">
            {groupHeadingLabel}
          </Heading>
          {canRemove && (
            <RemoveRoomButton classNames="margin-l" roomIndex={roomIndex} />
          )}
        </div>
      )}
      {hasRooms
      && (<NotesSection id="must_be_one_adult" />)}
      <StepperRow
        className={clsx(hasRooms ? 'pad-b' : 'margin-1-t')}
        rowLabelId="adult"
        globalisedAria={globalisedAdultAria}
        rowSubLabelId="adult_age_range"
        defaultValue={DEFAULT_ADULT_TRAVELING}
        isMultiRoom={isMultiRoom}
        min={MIN_ADULT_TRAVELING}
        max={maxAdultsForStepper}
        value={adults}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateAdults)}
        roomIndex={roomIndex}
      />
      {!hasRooms && <hr className="margin-tb" />}
      <StepperRow
        className={hasRooms ? 'pad-b' : 'margin-tb'}
        rowLabelId="child"
        globalisedAria={globalisedChildAria}
        rowSubLabelId={childAgeRangeLabel}
        defaultValue={DEFAULT_CHILDREN_TRAVELING}
        isMultiRoom={isMultiRoom}
        min={MIN_CHILDREN_TRAVELING}
        max={maxChildrenForStepper}
        value={children.length}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateChildCount)}
        roomIndex={roomIndex}
      />
      {!hasChildrenAges && children.length > 0 && (
        <ChildrenAgesVariant isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
      )}
      {hasInfants && (
        <>
          {!hasRooms && <hr className="margin-tb" />}
          <StepperRow
            className={hasRooms ? 'pad-b' : 'margin-tb'}
            rowLabelId="infant"
            globalisedAria={globalisedInfantAria}
            rowSubLabelId="age_under_2"
            defaultValue={DEFAULT_INFANTS_TRAVELING}
            isMultiRoom={isMultiRoom}
            min={MIN_INFANTS_TRAVELING}
            max={maxInfantsForStepper}
            value={infants.length}
            onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateInfantCount)}
            roomIndex={roomIndex}
          />
          {infants.length > 0 && (
            <ChildAgeNote infants={infants}>
              <InfantsVariant isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
            </ChildAgeNote>
          )}
        </>
      )}
      {!isMultiRoom && hasRooms && (
        <hr />
      )}
      {hasPets && (
        <>
          {!hasRooms && <hr className="margin-2-tb" />}
          <IncludePets
            roomIndex={roomIndex}
            value={includePets}
            onPetsChange={updatePets}
          />
        </>
      )}
    </div>
  );
});

RoomVariant.displayName = 'Room';

RoomVariant.propTypes = {
  canRemove: PropTypes.bool,
  isMultiRoom: PropTypes.bool,
  roomIndex: PropTypes.number.isRequired,
};

RoomVariant.defaultProps = {
  canRemove: false,
  isMultiRoom: false,
};

export default RoomVariant;
